import React, {
  useEffect,
} from 'react';
import clsx from 'clsx';
import Tooltip from 'core/components/Tooltip';
import {
  useConfirmDialog,
} from 'core/hooks';
import {
  useGetAppointmentClaimsSummary,
} from 'pages/Dashboard/pages/Billing/hooks';
import {
  billingEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import SwitchButton from 'core/components/Switch';

type Props = {
  encounterId: number;
  value: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
  shiftedToPatient?: boolean;
};

export default function OutOfPocketCheckbox({
  encounterId,
  value,
  onChange,
  shiftedToPatient = false,
  disabled: _disabled,
}: Props) {
  const { confirm, closeConfirm } = useConfirmDialog();
  const {
    data,
    isLoading,
    refetch,
  } = useGetAppointmentClaimsSummary(encounterId ?? 0, !shiftedToPatient);

  useEffect(() => {
    const unsubscribeFromChargesInfoChange = billingEventChannel.on('onChargesInfoChange', (entity) => {
      if (entity?.encounterId === encounterId) {
        refetch();
      }
    });

    return () => {
      unsubscribeFromChargesInfoChange();
    };
  }, [encounterId]);

  const { isEOBPosted, claimsCount = 0 } = data ?? {};
  const disabled = _disabled || isLoading || isEOBPosted;

  return (
    <Tooltip
      title={(
        <div className="text-xs flex flex-col">
          <span>
            Cannot mark as
            {' '}
            <span className="font-semibold">Out-of-pocket</span>
            .
          </span>
          <span>
            {shiftedToPatient
              ? 'Shifted to patient\'s responsibility.'
              : 'An EOB has already been posted.'}
          </span>
        </div>
      )}
      hide={!isEOBPosted && !shiftedToPatient}
    >
      <div className={clsx('w-fit', { 'cursor-not-allowed': disabled })}>
        <SwitchButton
          checked={value}
          disabled={disabled}
          label="Out-of-pocket"
          onChange={(event, checked: boolean) => {
            if (checked && claimsCount > 0) {
              confirm({
                content: (
                  <div className="text-sm flex flex-col gap-2">
                    <p>
                      {claimsCount > 1 ? (
                        <>
                          Multiple claims exist for this encounter. Marking it as
                          {' '}
                          <span className="font-semibold"> Out-of-pocket</span>
                          {' '}
                          will delete all associated claims.
                        </>
                      ) : (
                        <>
                          A claim exists for this encounter. Marking it as
                          {' '}
                          <span className="font-semibold"> Out-of-pocket</span>
                          {' '}
                          will delete the claim.
                        </>
                      )}
                    </p>
                    <p>Are you sure you want to continue?</p>
                  </div>
                ),
                onConfirm: async () => {
                  onChange(checked);
                  closeConfirm();
                },
                title: 'Mark as Out-of-pocket',
                confirmText: 'Confirm',
                cancelText: 'Close',
              });
            } else {
              onChange(checked);
            }
          }}
        />
      </div>
    </Tooltip>
  );
}
